import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";

const categories = ["All", "Enterprise Software", "Cybersecurity", "Digital Marketing"];

export default function Portfolio() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    api.get("/portfolio").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  const filtered = filter === "All" ? items : items.filter((p) => p.category === filter);

  return (
    <div data-testid="portfolio-page">
      {/* HEADER */}
      <section className="relative border-b border-white/10 bg-[#050505] overflow-hidden">
        <div className="absolute inset-0 topo-grid opacity-30" />
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 py-24 md:py-28">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
            <div className="md:col-span-4">
              <p className="overline">Work</p>
            </div>
            <div className="md:col-span-8">
              <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-black text-white tracking-[-0.03em] leading-[0.95]" data-testid="portfolio-title">
                Shipped. Scaled.<br/>
                <span className="text-[#ff3333]">Still standing.</span>
              </h1>
              <p className="mt-8 text-lg text-zinc-400 max-w-2xl leading-relaxed">
                A selection of production systems delivered in weeks, not quarters. Every project below ships with senior engineering on the line and measurable outcomes on the board.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FILTER */}
      <section className="border-b border-white/10 bg-[#050505] sticky top-16 z-30 backdrop-blur-md">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-4 flex flex-wrap items-center gap-3">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              data-testid={`portfolio-filter-${c.toLowerCase().replace(/\s+/g, '-')}`}
              className={`px-4 py-2 text-xs mono uppercase tracking-[0.18em] border transition-colors ${
                filter === c ? "bg-[#ff3333] text-white border-[#ff3333]" : "bg-transparent text-zinc-400 border-white/10 hover:text-white hover:border-white/30"
              }`}
            >
              {c}
            </button>
          ))}
          <span className="ml-auto text-xs mono text-zinc-600">{filtered.length} CASE{filtered.length === 1 ? "" : "S"}</span>
        </div>
      </section>

      {/* GRID */}
      <section className="py-20">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-2 gap-8">
          {filtered.map((p, i) => (
            <motion.article
              key={p.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.06 }}
              className="group relative bg-[#0a0a0a] border border-white/10 hover:border-[#ff3333] transition-colors overflow-hidden"
              data-testid={`portfolio-card-${p.id}`}
            >
              <div className="aspect-[16/10] overflow-hidden bg-black">
                <img src={p.image_url} alt={p.title} className="w-full h-full object-cover opacity-70 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500" />
              </div>
              <div className="p-8">
                <div className="flex items-center justify-between">
                  <p className="overline text-[#ff3333]">{p.category}</p>
                  <span className="text-xs mono text-zinc-500">{p.duration}</span>
                </div>
                <h3 className="mt-3 font-display text-2xl lg:text-3xl font-black text-white tracking-tight leading-tight">{p.title}</h3>
                <p className="mt-3 text-zinc-400 leading-relaxed">{p.summary}</p>
                <div className="mt-5 border-t border-white/5 pt-5">
                  <p className="text-xs mono text-zinc-500 uppercase tracking-[0.2em]">Outcome</p>
                  <p className="mt-1 text-white font-semibold">{p.outcome}</p>
                </div>
                <div className="mt-5 flex flex-wrap gap-2">
                  {p.tags.map((t) => (
                    <span key={t} className="text-xs mono text-zinc-400 border border-white/10 px-2 py-1">{t}</span>
                  ))}
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0a0a0a] border-t border-white/10 py-20">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <h2 className="font-display text-3xl md:text-4xl font-black text-white tracking-tight leading-tight max-w-2xl">
            Yours could be next. <span className="text-[#ff3333]">Shipping in weeks.</span>
          </h2>
          <Link to="/contact" className="btn-primary" data-testid="portfolio-cta">
            Start a project <ArrowUpRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
